/**
 * uploadFileKind.ts — decide which importer an uploaded sample file goes to,
 * by CONTENT first and extension second (2026-09-10).
 *
 * The lab's real files do not always carry the right extension: Gigi's
 * "2023_Donlin_KeyLAB_Test_Request.csv" is an Excel workbook (zip signature
 * `PK..`) saved with a .csv name. Trusting the extension sent it to the CSV
 * parser, which choked on binary. Every .xlsx / .xlsm is a zip, and the
 * ancient binary .xls is an OLE compound file — both are recognisable from
 * their first bytes regardless of what the file is called.
 */

export type UploadFileKind = 'workbook' | 'csv' | 'unknown';

/** Bytes needed to classify a file (zip and OLE signatures are 4 / 8 bytes). */
export const FILE_HEAD_BYTES = 8;

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]; // "PK\x03\x04" — .xlsx / .xlsm
const OLE_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]; // legacy .xls

const WORKBOOK_EXT = /\.(xlsx|xlsm|xls)$/i;
const CSV_EXT = /\.(csv|txt)$/i;

function startsWith(head: Uint8Array, magic: number[]): boolean {
  return head.length >= magic.length && magic.every((b, i) => head[i] === b);
}

/**
 * Classify by signature, then by extension. A zip/OLE signature wins even
 * under a .csv name; a .csv/.txt name with no binary signature is CSV; a
 * workbook extension with no signature is still handed to the workbook
 * importer so the server can explain what is wrong with it.
 */
export function detectUploadFileKind(fileName: string, head: Uint8Array): UploadFileKind {
  if (startsWith(head, ZIP_MAGIC) || startsWith(head, OLE_MAGIC)) return 'workbook';
  if (CSV_EXT.test(fileName)) return 'csv';
  if (WORKBOOK_EXT.test(fileName)) return 'workbook';
  return 'unknown';
}

/** The first FILE_HEAD_BYTES of a browser File. */
export async function readFileHead(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, FILE_HEAD_BYTES).arrayBuffer());
}
